/**
 * VARIANT COMBINATION GUARD
 *
 * Keeps the variant picker on a combination that exists, for products whose options form a sparse matrix (each
 * value of option 2 pairs with exactly one value of option 1 — a hair colour where every shade belongs to one
 * range, for example).
 *
 * Selecting a value of option 1 leaves the currently selected value of option 2 untouched, so the picker asks
 * Shopify for a pair that has no variant. Shopify preserves the requested values rather than resolving them to
 * the nearest match, so the section renders as unavailable. This guard rewrites such a request to the first
 * value of option 2 that belongs to the newly selected value of option 1.
 *
 * The listener runs on `document` in the capture phase, which always precedes the picker's own delegated handler
 * on `document.body`, so the stale pair is replaced before it can be read.
 *
 * Expects, from snippets/variant-picker.liquid:
 *   data-form-id         the id of the product form this guard belongs to
 *   data-combination-map JSON, keyed by option 1 value id, holding the option 2 value ids it pairs with
 */

const OPTION_INPUT_SELECTOR = 'input[data-option-position]';

class VariantCombinationGuard extends HTMLElement {
  requestToken = 0;

  connectedCallback() {
    const { formId, combinationMap } = this.dataset;

    this.formId = formId;
    this.combinations = this.parseCombinations(combinationMap);

    if (!this.formId || !this.combinations) return;

    document.addEventListener('change', this.onOptionChange, true);
    document.addEventListener('pointerenter', this.onOptionPreload, true);
    document.addEventListener('touchstart', this.onOptionPreload, true);
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onOptionChange, true);
    document.removeEventListener('pointerenter', this.onOptionPreload, true);
    document.removeEventListener('touchstart', this.onOptionPreload, true);
  }

  parseCombinations = (value) => {
    if (!value) return null;

    try {
      const parsed = JSON.parse(value);
      return Object.keys(parsed).length > 0 ? parsed : null;
    } catch {
      return null;
    }
  };

  get picker() {
    return document.querySelector(`variant-picker[form-id="${this.formId}"]`);
  }

  /**
   * True for an option input that belongs to this guard's form and sits at the given option position.
   *
   * Reads the form's id via getAttribute rather than the `.id` property: Shopify's product form always carries
   * a hidden `id` field (the selected variant id, for /cart/add), and HTMLFormElement exposes named controls as
   * properties of the form itself, shadowing even its own inherited ones — so `input.form.id` resolves to that
   * hidden input element, not the form's id string, on every product form on this theme.
   */
  isOwnOptionInput = (input, position) =>
    input?.matches?.(OPTION_INPUT_SELECTOR) === true &&
    input.dataset.optionPosition === position &&
    input.form?.getAttribute('id') === this.formId;

  onOptionChange = (event) => {
    const input = event.target;

    if (!this.isOwnOptionInput(input, '1')) return;

    const pairedIds = this.combinations[input.value];
    const selectedSecondValue = document.querySelector(
      `${OPTION_INPUT_SELECTOR}[data-option-position="2"][form="${this.formId}"]:checked`
    );

    // Nothing to correct when the pair is already valid, or when the map does not cover this value
    if (!pairedIds?.length || pairedIds.includes(selectedSecondValue?.value)) return;

    const { picker } = this;

    if (typeof picker?.selectCombination !== 'function') return;

    // Take over from the picker's own handler, which would otherwise read the stale pair off the DOM
    event.stopPropagation();

    // A corrected combination is a full section re-render, same as any other variant switch on this theme, and
    // gives no feedback of its own while it is in flight. Reuse the theme's own global loading-bar affordance
    // (already wired to these two events for the cart drawer, quick buy and quick order list) so the wait reads
    // as "loading" rather than an unresponsive click.
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    // Rapid option switching can have an older correction resolve after a newer one. We can't cancel the
    // in-flight fetch inside the picker's own selectCombination, but we can stop a stale response from hiding
    // the loading bar while a newer request is still pending: only the call that is still the latest when it
    // settles is allowed to dispatch theme:loading:end.
    const requestToken = ++this.requestToken;

    picker
      .selectCombination({
        optionValues: [input.value, pairedIds[0]],
        productChange: false,
      })
      .finally(() => {
        if (requestToken !== this.requestToken) return;
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      });
  };

  /**
   * The picker prefetches a combination on hover by pairing the hovered value with the values currently selected
   * for the other options. For a value of option 1 that always describes a combination the shopper cannot reach,
   * so the prefetch is discarded and the request wasted. Suppress it.
   */
  onOptionPreload = (event) => {
    const label = event.target instanceof Element ? event.target.closest('label') : null;

    if (this.isOwnOptionInput(label?.control, '1')) {
      event.stopPropagation();
    }
  };
}

if (!customElements.get('variant-combination-guard')) {
  customElements.define('variant-combination-guard', VariantCombinationGuard);
}
